<script setup>
import { computed } from 'vue'

/**
 * Branded primary action button. `bg-primary-container` orange with Teko
 * uppercase label. The canonical "do this thing" CTA across the app.
 *
 * Size scale (matches the typography ladder elsewhere - sm/md/lg/xl):
 *   - sm  → inline actions inside tables/cards
 *   - md  → page-level CTAs on Studio headers
 *   - lg  → hero CTAs on landing surfaces
 *   - xl  → auth + form submit (default - historical hero size)
 *
 * Disabled state previously dropped to 50% opacity which collapsed the
 * orange-on-zinc-950 contrast below WCAG AA (~2.1:1). The new disabled
 * style switches to neutral grays - same readability, semantically clear
 * the action is unavailable, no orange flashes during loading states.
 */
const props = defineProps({
    type: {
        type: String,
        default: 'submit',
    },
    /**
     * `xl` is the historical default (auth screens, form submits) - kept as
     * the default so existing call sites stay visually unchanged. Studio
     * chrome uses `md`; inline card/table actions use `sm`.
     */
    size: {
        type: String,
        default: 'xl',
        validator: (v) => ['sm', 'md', 'lg', 'xl'].includes(v),
    },
})

const SIZE_CLASSES = {
    sm: 'px-4 py-2 text-sm',
    md: 'px-5 py-3 text-base',
    lg: 'px-7 py-4 text-xl',
    xl: 'px-8 py-5 text-2xl',
}

const sizeClass = computed(() => SIZE_CLASSES[props.size])
</script>

<template>
    <button
        class="teko-font bg-primary-container text-on-primary-container shadow-primary-container/10 hover:bg-primary-container/90 inline-flex items-center justify-center gap-2 rounded-md font-bold tracking-widest uppercase shadow-lg transition-all duration-200 active:scale-[0.98] disabled:cursor-not-allowed disabled:bg-zinc-800 disabled:text-zinc-400 disabled:shadow-none"
        :class="sizeClass"
        :type="type"
    >
        <slot />
    </button>
</template>
